import React from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import { theme, media } from '../styles/theme';
import Layout from '../components/layout/Layout';

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem 1.5rem;

  ${media.mobile} {
    padding: 1.5rem 1rem;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.lg};
  text-align: center;

  ${media.mobile} {
    font-size: ${theme.fontSizes.xxl};
  }
`;

const Intro = styled.p`
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.gray.dark};
  text-align: center;
  line-height: 1.7;
  margin-bottom: ${theme.spacing.xl};
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;

  ${media.mobile} {
    font-size: ${theme.fontSizes.md};
  }
`;

const SectionCard = styled.div`
  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.md};

  ${media.mobile} {
    padding: ${theme.spacing.md};
  }
`;

const SectionTitle = styled.h2`
  font-size: ${theme.fontSizes.xl};
  font-weight: ${theme.fontWeights.medium};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.sm};

  ${media.mobile} {
    font-size: ${theme.fontSizes.lg};
  }
`;

const SectionText = styled.p`
  font-size: ${theme.fontSizes.md};
  color: ${theme.colors.gray.dark};
  line-height: 1.7;
`;

const FormatList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.xs};
  margin-top: ${theme.spacing.sm};
`;

const FormatBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  background-color: ${theme.colors.secondary};
  color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.medium};
`;

const AboutPage: React.FC = () => {
  return (
    <Layout>
      <Helmet>
        <title>Om Tryllepapp - Norges MTG-buylist</title>
        <meta name="description" content="Vi tilbyr det beste utvalget og den enkleste innbytteopplevelsen for Magic: The Gathering i Norge. Les om vår visjon og hva vi tilbyr." />
        <link rel="canonical" href="https://selg.tryllepapp.no/om-oss" />
      </Helmet>
      <Container>
        <Title>Om oss</Title>
        <Intro>
          Tryllepapp er en norsk plattform bygget for Magic: The Gathering-spillere
          og samlere som ønsker en enkel og oversiktlig måte å omsette kortene sine på.
        </Intro>

        <SectionCard>
          <SectionTitle>Vår visjon</SectionTitle>
          <SectionText>
            Vi ønsker å være en arena der spillere og samlere enkelt kan bytte
            kort de ikke trenger mot kort de trenger. Målet vårt er å gjøre
            prosessen så smidig som mulig, slik at du kan fokusere på det som
            betyr mest — å spille og samle.
          </SectionText>
        </SectionCard>

        <SectionCard>
          <SectionTitle>Bred dekning av formater</SectionTitle>
          <SectionText>
            Vi jobber for å ha det beste utvalget i Norge på tvers av alle
            populære formater. Uansett hva du spiller, skal du finne det du
            leter etter hos oss.
          </SectionText>
          <FormatList>
            <FormatBadge>Old School</FormatBadge>
            <FormatBadge>Commander</FormatBadge>
            <FormatBadge>cEDH</FormatBadge>
            <FormatBadge>Premodern</FormatBadge>
            <FormatBadge>Modern</FormatBadge>
            <FormatBadge>Legacy</FormatBadge>
          </FormatList>
        </SectionCard>

        <SectionCard>
          <SectionTitle>Enkel innbytte</SectionTitle>
          <SectionText>
            Vi tilbyr den enkleste innbytteopplevelsen i Norge. Selg dine
            singles til oss og få rask utbetaling til konto — eller velg
            butikkreditt og få 10% ekstra verdi.
          </SectionText>
        </SectionCard>

        <SectionCard>
          <SectionTitle>Transparent prising</SectionTitle>
          <SectionText>
            Hos oss ser du nøyaktig hva du kan få for hvert enkelt kort før du
            bestemmer deg. Ingen skjulte gebyrer eller overraskelser — bare en
            rett frem og ærlig handelsprosess.
          </SectionText>
        </SectionCard>
      </Container>
    </Layout>
  );
};

export default AboutPage;
